import {BaseStage} from "./BaseStage";
import {loading} from "../../../helpers/loading";

class ShowSelectStage extends BaseStage {


    constructor(globalState) {
        super(globalState);

        this.domContainer = document.querySelector(`.stage.${this.getStageKey()}`)

        // Update layout heights
        this.updateLayoutHeights();
        window.addEventListener('resize', () => {
            this.updateLayoutHeights();
        });

        // Setup show select cards
        this.domContainer.querySelectorAll('.show').forEach(showContainer => {
            const showInput = showContainer.querySelector(`input`);

            showInput.addEventListener('change', () => {
                this.handleShowSelect(showContainer);
            })

            // Trigger show selection
            showContainer.addEventListener('click', () => {
                showInput.checked = true;
                showInput.dispatchEvent(new InputEvent('change'));
            })
        })

        // Run base class initializations
        this.initSharedComponents();
    }

    /**
     * @return {PublicTicketsFormDefs.Stage}
     */
    getStageKey() {
        // noinspection JSValidateTypes
        return 'show-select';
    }

    display() {
        super.display();
        this.updateLayoutHeights();
    }

    /**
     * @param {HTMLElement} showContainer
     */
    handleShowSelect(showContainer) {
        /** @var {HTMLInputElement} */
        const showInput = showContainer.querySelector(`input`);

        // Clear all other shows from selection
        this.domContainer.querySelectorAll('.show').forEach(show => {
            show.classList.remove('selected');
            show.querySelector('input').checked = false;
        })

        // Mark show as selected
        showInput.checked = true;
        // And add selected class
        showContainer.classList.add('selected');

        // Store selected event
        const selectedEvent = this.getEventFromInput(showInput);

        this.globalState.setParentEvent(selectedEvent);

        if (!this.shouldAutoSkipDeterministicSelection()) {
            this.enableNextStageButton();
            return;
        }

        const deterministicSelection = this.getDeterministicSelectionFromEvent(selectedEvent);

        if (deterministicSelection) {
            this.globalState.setEventDate(deterministicSelection.date);

            if (deterministicSelection.seatType) {
                // noinspection JSCheckFunctionSignatures
                this.globalState.setSeatType(deterministicSelection.seatType);
            }
        }

        if (deterministicSelection?.canSkipDateSelect) {
            loading();

            const targetStage = deterministicSelection.seatType === 'reserved'
                ? 'seat-select'
                : 'seat-type-select';

            this.globalState.stageManager.goToStage(targetStage);

            return;
        }

        this.enableNextStageButton();
    }

    /**
     * @param {HTMLInputElement} input
     * @return {PublicEventResource.PublicEventResource}
     */
    getEventFromInput(input) {
        if (input.dataset.eventId) {
            return this.globalState.customerTicketSelection.events[input.dataset.eventId];
        }
        return JSON.parse(input.dataset.event);
    }

    shouldAutoSkipDeterministicSelection() {
        return true;
    }

    getDeterministicSelectionFromEvent(event) {
        const pendingDates = Array.isArray(event?.pendingDates)
            ? event.pendingDates
            : [];

        if (pendingDates.length !== 1) {
            return null;
        }

        const allDates = Array.isArray(event?.allDates)
            ? event.allDates
            : [];

        const selectedDate = pendingDates[0];
        const seatType = this.getDeterministicSeatTypeFromDate(selectedDate);
        const canSkipDateSelect = event?.dateSelectionType !== 'multiple_dates' || (
            event?.requireContinuousDateRange !== true && allDates.length === 1
        );

        if (!selectedDate) {
            return null;
        }

        return {
            date: selectedDate,
            seatType,
            canSkipDateSelect: canSkipDateSelect && !!seatType,
        };
    }

    getDeterministicSeatTypeFromDate(eventDate) {
        const hasGeneralAdmission = eventDate?.hasGeneralAdmissionTicketTypesAvailable === true;
        const hasReserved = eventDate?.hasReservedTicketTypesAvailable === true;

        if (hasGeneralAdmission === hasReserved) {
            return null;
        }

        return hasReserved
            ? 'reserved'
            : 'general-admission';
    }

    /**
     * Update the layout heights for horizontal layout
     */
    updateLayoutHeights() {
        if (!this.domContainer || !this.domContainer.classList.contains('horizontal-layout') || this.domContainer.classList.contains('d-none')) {
            return;
        }

        const navbar = document.querySelector('nav.navbar');
        const buttonsContainer = this.domContainer.querySelector('.buttons-container');

        const navbarHeight = (navbar && navbar.offsetHeight > 0) ? navbar.offsetHeight : 0;
        const buttonsHeight = (buttonsContainer && buttonsContainer.offsetHeight > 0) ? buttonsContainer.offsetHeight : 0;

        this.domContainer.style.setProperty('--navbar-height', `${navbarHeight}px`);
        this.domContainer.style.setProperty('--buttons-container-height', `${buttonsHeight}px`);
    }
}

export {ShowSelectStage};
